import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowLeft, Search, Leaf, AlertCircle } from "lucide-react";
import { usePageLoad } from "../hooks/usePageLoad";
import { useAuth } from "../context/AuthContext";
import SEO from "../components/SEO";
import Loading from "../components/Loading";

const NotFound: React.FC = () => {
  const { isLoaded } = usePageLoad();
  const { role } = useAuth();
  const isAdmin = role === "admin";

  if (!isLoaded) {
    return <Loading />;
  }

  return (
    <div>
      <SEO
        title="404 - Page Not Found | Golden Harvest Mango Farm"
        description="The page you're looking for doesn't exist. Return to Golden Harvest Mango Farm homepage or browse our premium mango varieties."
        url="/404"
        noindex={true}
        nofollow={true}
      />
      <section className="hero-surface section-padding bg-gradient-to-br from-mango-50 via-farm-50 to-golden-50 min-h-screen flex items-center">
        <div className="container-custom px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            {/* Animated 404 Number */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", stiffness: 140, damping: 16 }}
              className="relative mb-4"
            >
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-mango-500 via-golden-500 to-mango-600 leading-none tracking-tight">
                404
              </h1>
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="absolute -top-2 -right-2 md:-top-3 md:-right-3"
              >
                <Leaf className="h-8 w-8 md:h-10 md:w-10 text-mango-400 animate-float" />
              </motion.div>
              <motion.div
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -bottom-2 -left-2 md:-bottom-3 md:-left-3"
              >
                <Leaf
                  className="h-6 w-6 md:h-8 md:w-8 text-farm-400 animate-float"
                  style={{ animationDelay: "1s" }}
                />
              </motion.div>
            </motion.div>

            {/* Error Message */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="mb-4"
            >
              <div className="flex items-center justify-center mb-3">
                <AlertCircle className="h-5 w-5 text-mango-500 mr-2" />
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                  Page Not Found
                </h2>
              </div>
              <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
                Oops! The page you're looking for seems to have wandered off
                like a ripe mango in the orchard.
                <br />
                Don't worry, we'll help you find your way back!
              </p>
            </motion.div>

            {/* Animated Mango Illustration */}
            <motion.div
              initial={{ opacity: 0, rotate: -180 }}
              animate={{ opacity: 1, rotate: 0 }}
              transition={{ delay: 0.4, duration: 0.8, type: "spring" }}
              className="mb-6 flex justify-center"
            >
              <div className="relative">
                <motion.div
                  animate={{
                    y: [0, -10, 0],
                    rotate: [0, 4, -4, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="w-20 h-20 md:w-24 md:h-24 bg-gradient-to-br from-mango-400 to-golden-500 rounded-full relative shadow-xl"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-mango-300 to-mango-600 rounded-full opacity-50 blur-lg"></div>
                  <div className="absolute top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-mango-700 rounded-full"></div>
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 w-10 h-12 bg-mango-600 rounded-t-full"></div>
                </motion.div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            {!isAdmin && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="flex flex-col sm:flex-row gap-3 justify-center items-center mb-6"
              >
                <Link
                  to="/"
                  className="group relative inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-mango-500 to-golden-500 rounded-lg shadow-md hover:shadow-glow hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300 overflow-hidden"
                >
                  <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-mango-600 to-golden-600 transform scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-500"></span>
                  <Home className="h-4 w-4 mr-2 relative z-10" />
                  <span className="relative z-10">Go Home</span>
                </Link>

                <button
                  onClick={() => window.history.back()}
                  className="group inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-mango-600 bg-white border-2 border-mango-500 rounded-lg shadow-md hover:shadow-glow hover:bg-mango-50 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300"
                  aria-label="Go back to previous page"
                >
                  <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
                  Go Back
                </button>

                <Link
                  to="/mangoes"
                  className="group inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold text-farm-600 bg-white border-2 border-farm-500 rounded-lg shadow-md hover:shadow-glow-farm hover:bg-farm-50 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] transition-all duration-300"
                >
                  <Search className="h-4 w-4 mr-2" />
                  Browse Mangoes
                </Link>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default NotFound;
